/**
 * i18next bootstrap — single application instance, bundled resources only.
 * docs/NORMAX_PERFORMANCE_I18N_PHASE0_IMPLEMENTATION_PLAN.md §5.
 *
 * Imported as a side effect by LocaleProvider.tsx (module-scope import, so
 * `i18next.init()` runs before the first render that could call
 * useTranslation() — avoids the init-race risk documented in the plan §17.1).
 *
 * Phase 0 / Commit 1 note: resource JSON files are currently minimal
 * skeletons (see ./resources) — no screen content has been migrated yet.
 * That migration is Commit 2 of the approved 3-commit rollout.
 */
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import {
  FALLBACK_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  resolveInitialLocale,
} from "./locale";

import srCommon from "./resources/sr/common.json";
import srAuth from "./resources/sr/auth.json";
import srNavigation from "./resources/sr/navigation.json";
import srSettings from "./resources/sr/settings.json";
import srDashboard from "./resources/sr/dashboard.json";
import srWorkers from "./resources/sr/workers.json";
import srMasterData from "./resources/sr/masterData.json";
import srDailyEntry from "./resources/sr/dailyEntry.json";
import srAnalytics from "./resources/sr/analytics.json";
import srReports from "./resources/sr/reports.json";

import hrCommon from "./resources/hr/common.json";
import hrAuth from "./resources/hr/auth.json";
import hrNavigation from "./resources/hr/navigation.json";
import hrSettings from "./resources/hr/settings.json";
import hrDashboard from "./resources/hr/dashboard.json";
import hrWorkers from "./resources/hr/workers.json";
import hrMasterData from "./resources/hr/masterData.json";
import hrDailyEntry from "./resources/hr/dailyEntry.json";
import hrAnalytics from "./resources/hr/analytics.json";
import hrReports from "./resources/hr/reports.json";

import enCommon from "./resources/en/common.json";
import enAuth from "./resources/en/auth.json";
import enNavigation from "./resources/en/navigation.json";
import enSettings from "./resources/en/settings.json";
import enDashboard from "./resources/en/dashboard.json";
import enWorkers from "./resources/en/workers.json";
import enMasterData from "./resources/en/masterData.json";
import enDailyEntry from "./resources/en/dailyEntry.json";
import enAnalytics from "./resources/en/analytics.json";
import enReports from "./resources/en/reports.json";

/** Defensive localStorage read — matches the existing draft-storage.ts
 * try/catch pattern; a private-mode/quota exception must never break
 * bootstrap, it just means "no stored preference". */
function readStoredLocale(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function readNavigatorLanguages(): readonly string[] {
  try {
    return navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];
  } catch {
    return [];
  }
}

export const initialLocale = resolveInitialLocale({
  storedValue: readStoredLocale(),
  navigatorLanguages: readNavigatorLanguages(),
});

void i18next.use(initReactI18next).init({
  resources: {
    sr: {
      common: srCommon,
      auth: srAuth,
      navigation: srNavigation,
      settings: srSettings,
      dashboard: srDashboard,
      workers: srWorkers,
      masterData: srMasterData,
      dailyEntry: srDailyEntry,
      analytics: srAnalytics,
      reports: srReports,
    },
    hr: {
      common: hrCommon,
      auth: hrAuth,
      navigation: hrNavigation,
      settings: hrSettings,
      dashboard: hrDashboard,
      workers: hrWorkers,
      masterData: hrMasterData,
      dailyEntry: hrDailyEntry,
      analytics: hrAnalytics,
      reports: hrReports,
    },
    en: {
      common: enCommon,
      auth: enAuth,
      navigation: enNavigation,
      settings: enSettings,
      dashboard: enDashboard,
      workers: enWorkers,
      masterData: enMasterData,
      dailyEntry: enDailyEntry,
      analytics: enAnalytics,
      reports: enReports,
    },
  },
  lng: initialLocale,
  fallbackLng: FALLBACK_LOCALE,
  supportedLngs: SUPPORTED_LOCALES as unknown as string[],
  ns: ["common", "auth", "navigation", "settings", "dashboard", "workers", "masterData", "dailyEntry", "analytics", "reports"],
  defaultNS: "common",
  interpolation: { escapeValue: false }, // React already escapes interpolated values
  returnNull: false,
  react: { useSuspense: false }, // resources are fully bundled — no lazy backend, no Suspense needed
  debug: false,
});

export default i18next;
